import Grid from '@material-ui/core/Grid';
import PeFormPassword from '@pe-libs/react-components/build/components/PeFormPassword';
import PeFormSelect from '@pe-libs/react-components/build/components/PeFormSelect';
import PeFormTextField from '@pe-libs/react-components/build/components/PeFormTextField';
import Check from '@pe-libs/react-components/build/components/PeIcons/Check.svg';
import InfoCircleOutlined from '@pe-libs/react-components/build/components/PeIcons/InfoCircleOutlined.svg';
import Times from '@pe-libs/react-components/build/components/PeIcons/Times.svg';
import PeTooltip from '@pe-libs/react-components/build/components/PeTooltip';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUserLocale } from '#modules/app/selectors';
import { getRegistrationData } from '#modules/auth/selectors';
import messages from './messages';
import Buttons from './pageElements/PeOnboardingButtons';
import OnboardingIntro from './pageElements/PeOnboardingIntro';
import Stepper from './pageElements/PeOnboardingStepper';
import useStyles from './styles';
import { OnboardingStepType } from './types';

const PeOnboardingPage1: OnboardingStepType = ({ formik, wizardMethods, actions }) => {
  const { attributes } = useSelector(getRegistrationData) ?? {};
  const firstNameInputReference = useRef<HTMLInputElement>(null);
  const formatMessage = useIntlMessage();
  const styles = useStyles();
  const locale = useSelector(getUserLocale);

  const salutationOptions = [
    { title: '-', labelId: '1', value: '-' },
    { title: formatMessage(messages.salutationoptionmr), labelId: '2', value: 'mr' },
    { title: formatMessage(messages.salutationoptionmrs), labelId: '3', value: 'mrs' },
  ];

  const titleOptions = [
    { title: '-', labelId: '1', value: '-' },
    { title: formatMessage(messages.titleoptionprof), labelId: '2', value: 'prof' },
    { title: formatMessage(messages.titleoptiondr), labelId: '3', value: 'dr' },
    { title: formatMessage(messages.titleoptionprofdr), labelId: '4', value: 'profdr' },
  ];

  useEffect(() => {
    if (attributes?.firstName && !formik.values.page1.firstName) {
      formik.setFieldValue('page1.firstName', attributes.firstName);
    }

    if (attributes?.lastName && !formik.values.page1.lastName) {
      formik.setFieldValue('page1.lastName', attributes.lastName);
    }
  }, [attributes]);

  useEffect(() => {
    if (firstNameInputReference?.current) {
      firstNameInputReference.current.focus();
    }
  }, []);

  const getPasswordValidationStyles = (type: 'uppercase' | 'special' | 'number' | 'length') => {
    const isTouched = formik?.touched?.page1?.password;
    const isValid = !formik?.errors?.page1?.password?.includes(type) && formik?.values?.page1?.password;

    if (!isTouched && !isValid) {
      return {
        className: styles.passwordRule,
        icon: <Times />,
      };
    }

    return isValid
      ? { className: styles.passwordSuccess, icon: <Check /> }
      : { className: styles.passwordError, icon: <Times /> };
  };

  return (
    <Grid container={true} spacing={3} className={styles.containerStep1}>
      <Grid item={true} xs={12} className={styles.onboardingIntro}>
        <OnboardingIntro title={formatMessage(messages.titlestep1)} desc={formatMessage(messages.descstep1)} />
        <Stepper {...wizardMethods} actions={actions} />
      </Grid>
      <Grid item={true} xs={12} md={locale === 'de-de' ? 6 : 12}>
        <PeFormSelect
          formik={formik}
          name="page1.salutation"
          id="salutation"
          label={formatMessage(messages.salutationlabel)}
          items={salutationOptions}
        />
      </Grid>
      {locale === 'de-de' && (
        <Grid item={true} xs={12} md={6}>
          <PeFormSelect
            formik={formik}
            name="page1.title"
            id="title"
            label={formatMessage(messages.titlelabel)}
            items={titleOptions}
          />
        </Grid>
      )}
      <Grid item={true} xs={12}>
        <PeFormTextField
          formik={formik}
          inputRef={firstNameInputReference}
          type="text"
          name="page1.firstName"
          label={`${formatMessage(messages.firstnamelabel)}*`}
          autoComplete="given-name"
        />
      </Grid>
      <Grid item={true} xs={12}>
        <PeFormTextField
          formik={formik}
          type="text"
          name="page1.lastName"
          label={`${formatMessage(messages.lastnamelabel)}*`}
          autoComplete="family-name"
        />
      </Grid>
      {!formik.initialValues.page1.externalAuth && (
        <Grid item={true} xs={12}>
          <div className={styles.passwordContainer}>
            <input className={styles.fakeUsername} type="text" name="fakeusernameremembered" tabIndex={-999} />
            <PeFormPassword
              formik={formik}
              name="page1.password"
              label={`${formatMessage(messages.passwordlabel)}*`}
              autoComplete="new-password"
              className={styles.password}
            />
          </div>
        </Grid>
      )}
      <div className={styles.requiredText}>{formatMessage(messages.required)}</div>
      <Grid item={true} xs={12}>
        <div className={styles.passwordRules}>
          <div className={styles.passwordInfo}>
            {formatMessage(messages.passwordInfo)}
            <PeTooltip
              placement="top"
              className={styles.tooltip}
              interactive={true}
              title={formatMessage(messages.passwordTooltipText)}
            >
              <InfoCircleOutlined />
            </PeTooltip>
          </div>
          <div className={getPasswordValidationStyles('uppercase').className}>
            <div className={styles.iconValidation}>{getPasswordValidationStyles('uppercase').icon}</div>
            {formatMessage(messages.passwordUppercaseCharacter)}
          </div>
          <div className={getPasswordValidationStyles('special').className}>
            <div className={styles.iconValidation}>{getPasswordValidationStyles('special').icon}</div>
            {formatMessage(messages.passwordSpecialCharacter)}
          </div>
          <div className={getPasswordValidationStyles('number').className}>
            <div className={styles.iconValidation}>{getPasswordValidationStyles('number').icon}</div>
            {formatMessage(messages.passwordNumber)}
          </div>
          <div className={getPasswordValidationStyles('length').className}>
            <div className={styles.iconValidation}>{getPasswordValidationStyles('length').icon}</div>
            {formatMessage(messages.password10Characters)}
          </div>
        </div>
      </Grid>
      <div className={styles.infoText}>{formatMessage(messages.wizardinfo)}</div>
      <Buttons {...wizardMethods} />
    </Grid>
  );
};

export default PeOnboardingPage1;
